<script setup lang="ts">
interface SortOption {
  key: string
  label: string
  [key: string]: unknown
}

defineProps<{
  sort: string
  sortOptions: SortOption[]
}>()

defineEmits(['updateSort'])

const { breakpoint } = useApp()
const sorter = ref(null)
const isOpen = ref(false)

onClickOutside(sorter, () => {
  if (!['mobile', 'phablet', 'tablet'].includes(breakpoint.value)) {
    closeSorter()
  }
})

const closeSorter = () => {
  isOpen.value = false
}

const onToggle = (event: ToggleEvent) => {
  isOpen.value = event.newState === 'open'
}

onMounted(() => {
  watchEffect(() => {
    isOpen.value = ['mobile', 'phablet', 'tablet'].includes(breakpoint.value)
  })
})
</script>

<template>
  <div
    v-if="sort && sortOptions?.length && sortOptions.length > 1"
    ref="sorter"
    class="sort"
  >
    <UiCollapsable
      ref="sorter"
      :key="`${breakpoint}-${isOpen}`"
      :open="isOpen"
      @toggle="onToggle"
    >
      <template #label="{ open }">
        <span class="sort__label">{{ $t('Sort by') }}&nbsp;</span>
        <span class="sort__value">
          {{ $t(sortOptions.find(opt => opt.key === sort)?.label || '') }}
        </span>

        <NuxtIcon
          v-if="open"
          name="icon:min"
          class="collapsable__iconify"
        />
        <NuxtIcon
          v-else
          name="icon:plus"
          class="collapsable__iconify"
        />
      </template>
      <ul
        class="sort__select"
        role="menu"
        :aria-label="$t('Sort options')"
      >
        <li
          v-for="option in sortOptions"
          :key="`sort-${option.key}`"
          class="sort__option"
          :class="{ 'sort__options--selected': option.key === sort }"
          role="none"
        >
          <button
            type="button"
            class="sort__button"
            role="menuitem"
            :aria-current="option.key === sort ? 'true' : 'false'"
            :aria-label="`${$t('Sort by')} ${$t(option.label)}`"
            @click="$emit('updateSort', option.key)"
          >
            <NuxtIcon
              v-if="option.key === sort"
              name="icon:check"
              class="sort__icon icon icon--check"
              aria-hidden="true"
            />

            {{ $t(option.label) }}
          </button>
        </li>
      </ul>
    </UiCollapsable>
  </div>
</template>

<style>
@import url("~layers/components/Paginator/Sort.css");
</style>
